import { createFileRoute } from '@tanstack/react-router';
import {
  ActionIcon, Alert, Badge, Button, Card, Center, Group, Loader, Pagination,
  Paper, ScrollArea, Stack, Table, Text, Title, Tooltip,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconArrowsRightLeft, IconCalendar, IconDownload, IconInfoCircle, IconRefresh } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { notifications } from '@mantine/notifications';
import { fetchList } from '@/shared/api/jsonapi/client';
import { EmptyState } from '@/shared/components/EmptyState';
import { downloadCsv, type CsvColumn } from '@/shared/lib/exportCsv';
import type { Denormalized } from '@/shared/api/jsonapi/parser';

interface CrossTenantAccessLog extends Denormalized {
  type: 'cross-tenant-access-logs';
  /** Giorno aggregato (ISO date). */
  day?: string;
  date?: string;
  /** Utente che ha eseguito l'op. */
  actorUserId?: string;
  actorUsername?: string;
  actorEmail?: string;
  actorUser?: { id: string; email?: string; username?: string };
  /** Tenant origine (di norma == JWT tenant dell'utente). */
  sourceTenantId?: string;
  sourceTenant?: { id: string; name?: string };
  /** Tenant target (header inviato dal client). */
  targetTenantId?: string;
  targetTenant?: { id: string; name?: string };
  /** Numero di richieste aggregate quel giorno. */
  accessCount?: number;
  requestCount?: number;
  count?: number;
}

export const Route = createFileRoute('/_app/admin/cross-tenant-access-logs')({
  component: CrossTenantAccessLogsPage,
});

const PAGE_SIZE = 25;

const CSV_COLUMNS: Array<CsvColumn<CrossTenantAccessLog>> = [
  { header: 'Giorno', accessor: (r) => r.day ?? r.date },
  { header: 'Actor', accessor: (r) => r.actorUsername ?? r.actorEmail ?? r.actorUser?.username ?? r.actorUser?.email ?? r.actorUserId },
  { header: 'Tenant origine', accessor: (r) => r.sourceTenant?.name ?? r.sourceTenantId },
  { header: 'Tenant target', accessor: (r) => r.targetTenant?.name ?? r.targetTenantId },
  { header: 'N. richieste', accessor: (r) => r.accessCount ?? r.requestCount ?? r.count ?? 0 },
];

function CrossTenantAccessLogsPage() {
  const qc = useQueryClient();
  const [range, setRange] = useState<[Date | null, Date | null]>([null, null]);
  const [page, setPage] = useState(1);

  const listQ = useQuery({
    queryKey: ['cross-tenant-access-logs'],
    queryFn: () => fetchList<CrossTenantAccessLog>('cross-tenant-access-logs', {
      include: ['actorUser', 'sourceTenant', 'targetTenant'],
      page: { size: 500 },
    }),
  });

  const all = useMemo(() => listQ.data?.data ?? [], [listQ.data]);

  const filtered = useMemo(() => {
    const [from, to] = range;
    return all.filter((row) => {
      const d = row.day ?? row.date;
      if (!d) return true;
      if (from && new Date(d) < dayjs(from).startOf('day').toDate()) return false;
      if (to && new Date(d) > dayjs(to).endOf('day').toDate()) return false;
      return true;
    });
  }, [all, range]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageData = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // Esporta le righe correnti caricate (già filtrate client-side per periodo).
  const exportCsv = () => {
    if (filtered.length === 0) {
      notifications.show({ color: 'orange', message: 'Nessun dato da esportare' });
      return;
    }
    downloadCsv(`cross-tenant-access-logs-${dayjs().format('YYYYMMDD-HHmm')}`, CSV_COLUMNS, filtered);
    notifications.show({ color: 'green', message: `${filtered.length} righe esportate` });
  };

  return (
    <Stack>
      <Group justify="space-between" wrap="wrap" align="flex-end">
        <Group>
          <IconArrowsRightLeft size={28} />
          <div>
            <Title order={1}>Audit log cross-tenant</Title>
            <Text size="sm" c="dimmed">
              {all.length} aggregati giornalieri totali
            </Text>
          </div>
        </Group>
        <Group gap="xs">
          <Tooltip label="Aggiorna">
            <ActionIcon
              variant="light"
              size="lg"
              onClick={() => qc.invalidateQueries({ queryKey: ['cross-tenant-access-logs'] })}
            >
              <IconRefresh size={18} />
            </ActionIcon>
          </Tooltip>
          <Button
            size="xs"
            variant="default"
            leftSection={<IconDownload size={14} />}
            onClick={exportCsv}
            disabled={filtered.length === 0}
          >
            Esporta CSV
          </Button>
        </Group>
      </Group>

      <Alert color="blue" variant="light" icon={<IconInfoCircle size={16} />}>
        Storico delle operazioni eseguite in modalità cross-tenant tramite header <code>tenantId</code>.
        Solo lettura. Aggregato per giorno × actor × target.
      </Alert>

      <Card withBorder padding="md" radius="md">
        <Group wrap="wrap" gap="sm">
          <DatePickerInput
            type="range"
            placeholder="Periodo"
            value={range}
            onChange={(v) => setRange(v as [Date | null, Date | null])}
            leftSection={<IconCalendar size={14} />}
            clearable
            w={260}
            valueFormat="DD/MM/YYYY"
          />
          {filtered.length !== all.length && (
            <Badge variant="light" color="blue">{filtered.length} di {all.length}</Badge>
          )}
        </Group>
      </Card>

      {listQ.isLoading ? (
        <Center py="xl"><Loader /></Center>
      ) : listQ.isError ? (
        <Alert color="red">Errore caricamento log. Verifica i permessi.</Alert>
      ) : filtered.length === 0 ? (
        <Paper withBorder radius="md">
          <EmptyState
            icon={IconArrowsRightLeft}
            title={all.length === 0 ? 'Nessun accesso cross-tenant registrato' : 'Nessun risultato per il periodo selezionato'}
            description={all.length === 0
              ? 'Le operazioni cross-tenant compariranno qui (aggregate giornalmente) appena qualcuno userà l\'header tenantId.'
              : 'Prova ad allargare il periodo.'}
          />
        </Paper>
      ) : (
        <>
          <Card withBorder padding={0} radius="md">
            <ScrollArea type="auto">
              <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover stickyHeader>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Giorno</Table.Th>
                    <Table.Th>Actor</Table.Th>
                    <Table.Th>Tenant origine</Table.Th>
                    <Table.Th>Tenant target</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>N. richieste</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {pageData.map((row) => {
                    const day = row.day ?? row.date;
                    const actor = row.actorUsername ?? row.actorEmail ?? row.actorUser?.username ?? row.actorUser?.email ?? row.actorUserId ?? '—';
                    const source = row.sourceTenant?.name ?? row.sourceTenantId ?? '—';
                    const target = row.targetTenant?.name ?? row.targetTenantId ?? '—';
                    const count = row.accessCount ?? row.requestCount ?? row.count ?? 0;
                    return (
                      <Table.Tr key={row.id}>
                        <Table.Td>
                          <Text size="sm">{day ? dayjs(day).format('DD/MM/YYYY') : '—'}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" fw={500}>{actor}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs" c="dimmed">{source}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge color="grape" variant="light">{target}</Badge>
                        </Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>
                          <Text size="sm" fw={600}>{count}</Text>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          </Card>
          {totalPages > 1 && (
            <Group justify="center">
              <Pagination total={totalPages} value={safePage} onChange={setPage} size="sm" />
            </Group>
          )}
        </>
      )}
    </Stack>
  );
}
